// ─────────────────────────────────────────────────────────────────────────────
// QueueRecovery.jsx — Écran de reprise de la file d'attente locale (SPEC §6).
//
// Affiche les rapports écrits localement mais non confirmés (pending) ou en échec
// définitif (failed), et propose de les renvoyer. NON BLOQUANT : modale au-dessus,
// fermable ("Plus tard"). Réapparaît au prochain démarrage tant qu'il reste des
// entrées. Le rejeu applique un MERGE par recordId (§6.4) — jamais un écrasement
// de la clé rep7 — et n'écrit jamais le sid : la donnée reste au nom de l'auteur.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { listAll, getBlobs, setGarder, remove, isAvailable } from '../localQueue.js';
import { uploadPhotosToStorage } from '../supabase.js';

const LOCK_KEY = 'lc_queue_replay_lock';
const LOCK_MS = 30000;

const blobToDataUrl = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = () => rej(r.error || new Error('read'));
  r.readAsDataURL(blob);
});

function ageTxt(ts) {
  const ms = Date.now() - (ts || 0);
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'il y a moins d\'une heure';
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return `il y a ${j} j`;
}

export function QueueRecovery({ rep, setRep, biens = [], staff = [], sess }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyUid, setBusyUid] = useState(null);
  const [naDismissed, setNaDismissed] = useState(false);

  // Filtre sur rep7 OBLIGATOIRE (v130). listAll() rend TOUTES les clés de la file, et
  // depuis le Chantier B (01/08) la file contient aussi als7 et controles7 — qui sont
  // rejoués silencieusement par queueAutoReplay.js, sans écran ni clic. Cet écran-ci ne
  // sait traiter que rep7 (replay() route les photos en pk/gp et appelle setRep) : sans
  // ce filtre il affichait des pointages et des contrôles, et un clic sur « Renvoyer »
  // écrivait un contrôle dans rep7. Constaté en production le 03/08/2026.
  const refresh = async () => {
    try {
      const all = await listAll({ includeConfirmed: false });
      setEntries(all.filter(e => e && e.key === 'rep7'));
    }
    catch { setEntries([]); }
  };
  useEffect(() => { if (isAvailable()) refresh(); }, []);

  // IndexedDB indisponible (§8.1) : avertissement discret, une fois, dismissable.
  if (!isAvailable()) {
    if (naDismissed) return null;
    return (
      <div style={{ position: 'fixed', left: 8, right: 8, top: 8, zIndex: 9998, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>La sauvegarde locale de secours n'est pas disponible sur cet appareil.</span>
        <button onClick={() => setNaDismissed(true)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: '#92400e' }}>×</button>
      </div>
    );
  }

  if (!open || entries.length === 0) return null;

  const bienName = (bid) => (biens.find(b => b.id === bid) || {}).name || '?';
  const currentName = (staff.find(s => s.id === (sess && sess.id)) || {}).name || (sess && sess.name) || '';

  const replay = async (entry) => {
    // Garde (v130) : cette fonction est structurellement incapable de traiter autre chose
    // que rep7 — elle range les photos en pk/gp et écrit via setRep. Le filtre de refresh()
    // devrait suffire, mais on refuse explicitement plutôt que de corrompre rep7 en silence
    // si une entrée d'une autre clé arrive un jour par un autre chemin.
    if (!entry || entry.key !== 'rep7') {
      console.error('[QueueRecovery] entrée ignorée : clé non gérée par cet écran', entry && entry.key, entry && entry.recordId);
      return;
    }
    // Verrou coopératif multi-onglets (§8.3) — non destructeur car le merge est idempotent.
    const nowTs = Date.now();
    try {
      const raw = localStorage.getItem(LOCK_KEY);
      if (raw && (nowTs - Number(raw)) < LOCK_MS) {
        window.alert('Un renvoi est déjà en cours dans un autre onglet. Réessaie dans quelques secondes.');
        return;
      }
      localStorage.setItem(LOCK_KEY, String(nowTs));
    } catch { /* localStorage indisponible : on continue, le merge protège */ }

    setBusyUid(entry.uid);
    try {
      const blobs = await getBlobs(entry.uid);
      const pk = {}, gp = {};
      for (const b of blobs) {
        try { const durl = await blobToDataUrl(b.blob); (b.field === 'gp' ? gp : pk)[b.pointId] = durl; }
        catch (e) { console.error('[QueueRecovery] blob illisible', b.pointId, e); }
      }
      const upPk = await uploadPhotosToStorage(pk, entry.recordId, 'pk');
      const upGp = await uploadPhotosToStorage(gp, entry.recordId, 'gp');
      const full = { ...entry.payload, pk: upPk, gp: upGp };
      // MERGE par recordId (même logique que persistRep, StaffUI L51-52). Le sid du
      // payload n'est jamais réécrit → la donnée reste au nom de l'auteur réel.
      setRep(prev => {
        const i = prev.findIndex(x => x.id === full.id);
        return i >= 0 ? prev.map(x => x.id === full.id ? full : x) : [...prev, full];
      });
      // La confirmation (status → confirmed) viendra du succès de trySave via
      // confirmByRecordIds (§5.3). On rafraîchit la liste après un court délai.
    } catch (e) {
      console.error('[QueueRecovery] rejeu échoué', e);
      window.alert('Le renvoi a échoué : ' + ((e && e.message) || e) + '\nLe rapport reste conservé, tu peux réessayer.');
    } finally {
      try { localStorage.removeItem(LOCK_KEY); } catch { /* ignore */ }
      setBusyUid(null);
      setTimeout(refresh, 1200);
    }
  };

  const replayAll = async () => {
    for (const e of entries) { await replay(e); } // séquentiel, jamais en parallèle (§8.3)
  };

  const toggleGarder = async (e) => { await setGarder(e.uid, !e.garder); refresh(); };

  const del = async (e) => {
    if (!window.confirm('Supprimer définitivement ce rapport non transmis ?\n\nCette donnée sera perdue et ne pourra plus être renvoyée.')) return;
    await remove(e.uid);
    refresh();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(42,37,32,.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: 'var(--wh,#fff)', borderRadius: 14, maxWidth: 560, width: '100%', marginTop: 24, boxShadow: '0 12px 40px rgba(0,0,0,.35)', overflow: 'hidden' }}>
        <div style={{ background: '#7f1d1d', color: '#fff', padding: '14px 18px' }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>📥 Rapports en attente d'envoi</div>
          <div style={{ fontSize: 12, opacity: .9, marginTop: 2 }}>
            {entries.length} rapport{entries.length > 1 ? 's' : ''} enregistré{entries.length > 1 ? 's' : ''} sur cet appareil mais pas encore confirmé{entries.length > 1 ? 's' : ''} côté serveur.
          </div>
        </div>

        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '55vh', overflowY: 'auto' }}>
          {entries.map((e) => {
            const autreAuteur = e.sid && sess && e.sid !== sess.id;
            const nbPhotos = Array.isArray(e.photos) ? e.photos.length : 0;
            return (
              <div key={e.uid} style={{ border: '1px solid var(--li,#e5e0d8)', borderRadius: 10, padding: '10px 12px', background: e.status === 'failed' ? '#fef2f2' : 'var(--bg,#faf8f5)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{bienName(e.payload && e.payload.bid)}</div>
                  <div style={{ fontSize: 11, color: e.status === 'failed' ? 'var(--dg,#b91c1c)' : 'var(--si,#8a8178)' }}>
                    {e.status === 'failed' ? '⚠️ Échec' : '⏳ En attente'}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--si,#8a8178)', marginTop: 3 }}>
                  {e.authorLabel || 'Auteur inconnu'} · {(e.payload && e.payload.date) || ''} · {nbPhotos} photo{nbPhotos > 1 ? 's' : ''}{e.photosOmises ? ' (photos non incluses)' : ''} · {ageTxt(e.createdAt)}
                </div>
                {autreAuteur && (
                  <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '5px 8px', marginTop: 6 }}>
                    ⚠️ Saisi par <b>{e.authorLabel || '?'}</b>. Tu es connecté{currentName ? ' en tant que ' : ''}<b>{currentName}</b>. Le rapport sera enregistré au nom de {e.authorLabel || 'son auteur'}, l'envoi se fera depuis ta session.
                  </div>
                )}
                {e.lastError ? <div style={{ fontSize: 10, color: 'var(--si,#8a8178)', marginTop: 4, wordBreak: 'break-word' }}>{e.lastError}</div> : null}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  <button disabled={busyUid === e.uid} onClick={() => replay(e)} className="btn bor sm" style={{ fontSize: 12 }}>
                    {busyUid === e.uid ? 'Envoi…' : '↻ Renvoyer'}
                  </button>
                  <button onClick={() => toggleGarder(e)} className="btn bgh sm" style={{ fontSize: 12 }}>
                    {e.garder ? '★ Gardé' : '☆ Garder'}
                  </button>
                  <button onClick={() => del(e)} className="btn bgh sm" style={{ fontSize: 12, color: 'var(--dg,#b91c1c)' }}>🗑 Supprimer</button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderTop: '1px solid var(--li,#e5e0d8)' }}>
          <button onClick={() => setOpen(false)} className="btn bgh sm" style={{ fontSize: 13 }}>Plus tard</button>
          <button disabled={!!busyUid} onClick={replayAll} className="btn bor sm" style={{ fontSize: 13 }}>↻ Tout renvoyer</button>
        </div>
      </div>
    </div>
  );
}
